import React, { useState, useMemo, useEffect } from 'react';
import { withTranslation } from 'react-i18next';

import { connect } from 'react-redux';
import ListItem from '@mui/material/ListItem';
import List from '@mui/material/List';
import ListItemIcon from '@mui/material/ListItemIcon';
import Skeleton from '@mui/material/Skeleton';
import ListSubheader from '@mui/material/ListSubheader';

import { HistoryIcon } from 'components/Icons';
import ScrollOnMountElement from 'components/shared/ScrollOnMountElement';
import DescriptionTooltip from 'components/input/DescriptionTooltip';

import { fetchRecentQuestions } from 'store/modules/inputAnalysis';
import parseSuggestion from 'utils/suggestionParser';

import styles from '../empty-input-suggestions.scss';
import inputStyles from '../input.scss';

import { sx } from './EmptyInputSuggestions.styles';
import { DiscoveryButton, KgButton } from 'components/modals/HeaderButtonsModal/Buttons';

import HeaderButtonsModal from 'components/modals/HeaderButtonsModal/HeaderButtonsModal';

/**
 * This is the initial component that gets shown when someone focuses on the input field.
 *
 * It has three sections separated by a divider from bottom to top:
 * 1) three classic suggestions as we know it from the typicial auto complete, indicating starting points
 * 2) a list of the most recent searches
 * 3) two buttons:
 *    1) expand into discovery
 *    2) trigger knowledge graph view
 */
const EmptyInputSuggestions = ({
  t,
  recentQuestions,
  fetchingRecentQuestions,
  suggestions,
  dispatch,
  onSuggestionSelection,
  onClickAway
}) => {
  const [selectedSuggestion, setSelectedSuggestion] = useState(-1);
  const [numberOfSuggestionsToShow, setNumberOfSuggestionsToShow] = useState(3);
  const [numberOfRecentQuestionsToShow, setNumberOfRecentQuestionsToShow] = useState(3);

  // we combine the suggestions and recent questions into one array because we want to be able to navigate through them
  const combinedSuggestions = useMemo(() => {
    const slicedSuggestions = suggestions.slice(0, numberOfSuggestionsToShow);
    const slicedRecentQuestions = recentQuestions.slice(0, numberOfRecentQuestionsToShow);
    return [
      ...slicedSuggestions.map((s, i) => ({ ...s, type: 'suggestion', index: i })),
      ...slicedRecentQuestions.map((s, i) => ({ ...s, type: 'recentQuestion', index: i + slicedSuggestions.length }))
    ];
  }, [suggestions, recentQuestions, numberOfSuggestionsToShow, numberOfRecentQuestionsToShow]);

  // fetch recent questions on initial load
  useEffect(() => {
    dispatch(fetchRecentQuestions());
  }, []);

  // On Key up or down, change the selected suggestion
  const handleKeyDown = e => {
    if (combinedSuggestions.length === 0) {
      return;
    }
    if (e.key === 'ArrowDown') {
      // if selected suggestion is not set yet OR we're at the first one (that is 0) move to the last one
      // if we're not at the first one, move to the previous one
      if (selectedSuggestion === -1 || selectedSuggestion === 0) {
        setSelectedSuggestion(combinedSuggestions.length - 1);
      } else {
        setSelectedSuggestion(selectedSuggestion - 1);
      }
    } else if (e.key === 'ArrowUp') {
      setSelectedSuggestion((selectedSuggestion + 1) % combinedSuggestions.length);
    } else if (e.key === 'Escape') {
      onClickAway();
    }

    // if enter or tab is pressed, select the suggestion
    if ((e.key === 'Enter' || e.key === 'Tab') && selectedSuggestion !== -1) {
      onSuggestionSelection(e, combinedSuggestions[selectedSuggestion]);
    }
  };

  // add the keydown listener and remove it as soon as we leave the currentOpenModalStates.suggestions state
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedSuggestion, combinedSuggestions]);

  const recentQuestionsSubheader = useMemo(
    () => (
      <ListSubheader sx={sx.listSubheader}>
        <span className={styles.listSubtitle}>{t('tooltip.recent-questions')}</span>
        {!fetchingRecentQuestions && recentQuestions.length > numberOfRecentQuestionsToShow && (
          <span className={styles.showMore} onClick={() => setNumberOfRecentQuestionsToShow(recentQuestions.length)}>
            {t('show-more')}
          </span>
        )}
      </ListSubheader>
    ),
    [recentQuestions, numberOfRecentQuestionsToShow]
  );

  const recentQuestionsList = useMemo(() => {
    return (
      <List component="nav" disablePadding={true} dense={true} subheader={recentQuestionsSubheader}>
        {fetchingRecentQuestions ? (
          <>
            <ListItem sx={sx.listItem} key="skeleton_one">
              <Skeleton variant="rectangular" sx={sx.skeleton} style={{ maxWidth: '85%' }} />
            </ListItem>
            <ListItem sx={sx.listItem} key="skeleton_two">
              <Skeleton variant="rectangular" sx={sx.skeleton} style={{ maxWidth: '30%' }} />
            </ListItem>
          </>
        ) : (
          recentQuestions.length > 0 && (
            <>
              {combinedSuggestions
                .filter(s => s.type === 'recentQuestion')
                .reverse()
                .map(question => {
                  return (
                    <ListItem
                      key={question.index}
                      sx={sx.listItem}
                      tabIndex={null} // disable losing focus on input box when selecting a suggestion
                      selected={question.index === selectedSuggestion}
                      autoFocus={question.index === selectedSuggestion}
                      button
                      onClick={e => onSuggestionSelection(e, question)}
                    >
                      <ListItemIcon sx={sx.icon}>
                        <HistoryIcon />
                      </ListItemIcon>
                      <div className={styles.suggestion}>
                        {parseSuggestion(
                          question,
                          inputStyles._plain,
                          inputStyles._linked,
                          inputStyles._linkedDescription,
                          inputStyles._onlyShow,
                          inputStyles._icon
                        )}
                      </div>
                    </ListItem>
                  );
                })}
              {recentQuestions.length === numberOfRecentQuestionsToShow && <ScrollOnMountElement />}
            </>
          )
        )}
      </List>
    );
  }, [combinedSuggestions, selectedSuggestion, recentQuestions, fetchingRecentQuestions]);

  const suggestionsSubheader = useMemo(
    () => (
      <ListSubheader sx={sx.listSubheader}>
        <span className={styles.listSubtitle}>{t('you-could-start-with')}</span>
        {suggestions.length > numberOfSuggestionsToShow && (
          <span className={styles.showMore} onClick={() => setNumberOfSuggestionsToShow(suggestions.length)}>
            {t('show-more')}
          </span>
        )}
      </ListSubheader>
    ),
    [suggestions, numberOfSuggestionsToShow]
  );

  const suggestionsList = useMemo(
    () => (
      <List component="nav" disablePadding={true} dense={true} subheader={suggestionsSubheader}>
        {combinedSuggestions
          .filter(s => s.type === 'suggestion')
          .reverse()
          .map(suggestion => {
            const suggestionElement = (
              <ListItem
                tabIndex={null} // disable losing focus on input box when selecting a suggestion
                key={suggestion.index}
                sx={sx.listItem}
                selected={suggestion.index === selectedSuggestion}
                autoFocus={suggestion.index === selectedSuggestion}
                button
                onClick={e => onSuggestionSelection(e, suggestion)}
              >
                <div className={styles.suggestion}>
                  {parseSuggestion(
                    suggestion,
                    inputStyles._plain,
                    inputStyles._linked,
                    inputStyles._linkedDescription,
                    inputStyles._onlyShow,
                    inputStyles._icon
                  )}
                </div>
              </ListItem>
            );
            // Works for now only on keyboard navigation, not on mouse hover
            return suggestion.index === selectedSuggestion ? (
              <DescriptionTooltip suggestion={suggestion}>{suggestionElement}</DescriptionTooltip>
            ) : (
              suggestionElement
            );
          })}
        {suggestions.length === numberOfSuggestionsToShow && <ScrollOnMountElement />}
      </List>
    ),
    [combinedSuggestions, selectedSuggestion, suggestions]
  );

  return (
    <HeaderButtonsModal
      onClickAway={onClickAway}
      leftButton={<DiscoveryButton />}
      title=""
      rightButton={<KgButton />}
      content={
        <>
          {recentQuestionsList}
          {suggestions.length > 0 && suggestionsList}
        </>
      }
    />
  );
};

const mapStateToProps = state => ({
  fetchingRecentQuestions: state.inputAnalysis.fetchingRecentQuestions,
  recentQuestions: state.inputAnalysis.recentQuestions
});

export default withTranslation('veezoo')(connect(mapStateToProps)(EmptyInputSuggestions));
